// Body attitude for the thermal 3D view: the satellite's attitude data, the Earth-relative pose,
// and the maneuver events that drive the cube's animation through simulation time.
// quaternionData is flat [time, x, y, z, w, ...] (time is an ISO string); postionData is flat [time, x, y, z, ...]
// (time is seconds from the availability start). Thermal times are Unix seconds.

import * as THREE from "three";
import { AXES, THERMAL_BUS_AXES } from "../constants/thermalConstants";

interface AttitudeSeries {
    /** Unix seconds per sample */
    times: Float64Array;
    /** Four values per sample: x, y, z, w */
    quats: Float64Array;
    /** Satellite ECEF positions for the Earth-relative correction (null if unavailable) */
    positions: PositionSeries | null;
    /** Fixed start alignment (nadir at the first sample -> down), computed once per series */
    alignment: THREE.Quaternion;
    /** Local orbital frame at the first sample, computed once per series (null without positions) */
    refFrame: THREE.Quaternion | null;
}

interface AttitudeSample { t: number; x: number; y: number; z: number; w: number }

interface PositionSeries {
    /** Unix seconds per sample */
    times: Float64Array;
    /** Three values per sample: ECEF x, y, z */
    pos: Float64Array;
}

const QUAT_STRIDE = 5;
const POS_STRIDE = 4;

/** Availability start in Unix seconds, or null if it cannot be parsed. */
const availabilityStartSec = (availability: string | null | undefined): number | null => {
    if (!availability) return null;
    const ms = Date.parse(availability.split("/")[0]);
    return Number.isNaN(ms) ? null : ms / 1000;
};

const buildPositionSeries = (
    postionData: unknown[] | null | undefined,
    availability: string | null | undefined
): PositionSeries | null => {
    const startSec = availabilityStartSec(availability);
    if (!postionData || postionData.length < POS_STRIDE || startSec === null) return null;
    const count = Math.floor(postionData.length / POS_STRIDE);
    const times = new Float64Array(count);
    const pos = new Float64Array(count * 3);
    for (let i = 0; i < count; i++) {
        const base = i * POS_STRIDE;
        const raw = postionData[base];
        const t = typeof raw === "number" ? startSec + raw : Date.parse(String(raw)) / 1000;
        if (Number.isNaN(t)) return null;
        times[i] = t;
        pos[i * 3] = Number(postionData[base + 1]);
        pos[i * 3 + 1] = Number(postionData[base + 2]);
        pos[i * 3 + 2] = Number(postionData[base + 3]);
    }
    return { times, pos };
};

/** Builds the attitude series from the presim data; null if there is no usable attitude data. */
export const buildAttitudeSeries = (
    quaternionData: unknown[] | null | undefined,
    postionData?: unknown[] | null,
    availability?: string | null
): AttitudeSeries | null => {
    if (!quaternionData || quaternionData.length < QUAT_STRIDE) return null;
    const count = Math.floor(quaternionData.length / QUAT_STRIDE);
    const times = new Float64Array(count);
    const quats = new Float64Array(count * 4);
    for (let i = 0; i < count; i++) {
        const base = i * QUAT_STRIDE;
        const ms = Date.parse(String(quaternionData[base]));
        if (Number.isNaN(ms)) return null;
        times[i] = ms / 1000;
        quats[i * 4] = Number(quaternionData[base + 1]);
        quats[i * 4 + 1] = Number(quaternionData[base + 2]);
        quats[i * 4 + 2] = Number(quaternionData[base + 3]);
        quats[i * 4 + 3] = Number(quaternionData[base + 4]);
    }
    const positions = buildPositionSeries(postionData, availability);
    const first: AttitudeSample = { t: times[0], x: quats[0], y: quats[1], z: quats[2], w: quats[3] };
    return {
        times,
        quats,
        positions,
        alignment: alignmentFromSample(first),
        refFrame: positions ? localFrameQuaternion(positions, times[0]) : null,
    };
};

const sampleAt = (series: AttitudeSeries, i: number): AttitudeSample => ({
    t: series.times[i],
    x: series.quats[i * 4],
    y: series.quats[i * 4 + 1],
    z: series.quats[i * 4 + 2],
    w: series.quats[i * 4 + 3],
});

/** Nearest quaternion sample to a Unix-seconds time, clamped to the ends of the series. */
const findNearestAttitude = (series: AttitudeSeries, unixSec: number): AttitudeSample => {
    const { times } = series;
    const last = times.length - 1;

    if (unixSec <= times[0]) return sampleAt(series, 0);
    if (unixSec >= times[last]) return sampleAt(series, last);

    let lo = 0, hi = last;
    while (lo < hi - 1) {
        const mid = (lo + hi) >> 1;
        if (times[mid] <= unixSec) lo = mid; else hi = mid;
    }
    return unixSec - times[lo] <= times[hi] - unixSec ? sampleAt(series, lo) : sampleAt(series, hi);
};

// ---------------------------------------------------------------------------
// Axis offset (ported from viewer-packets.ts getInitialAxisOffsetQuaternion / applyInitialAxisOffset).
// Cesium's qFinal = qBackend * qOffset puts the body nadir on world down and the orbit axis on world +X.
// The thermal cube uses three.js axes, so two fixed conversions are applied on top.
// ---------------------------------------------------------------------------

// Cesium body-local axis vectors (getLocalAxis in viewer-packets.ts)
const CESIUM_LOCAL_AXIS: Record<string, THREE.Vector3> = {
    X: new THREE.Vector3(0, 1, 0), "-X": new THREE.Vector3(0, -1, 0),
    Y: new THREE.Vector3(-1, 0, 0), "-Y": new THREE.Vector3(1, 0, 0),
    Z: new THREE.Vector3(0, 0, 1), "-Z": new THREE.Vector3(0, 0, -1),
};

const parseDirection = (dir: string): { sign: number; letter: string } => ({
    sign: dir.startsWith("-") ? -1 : 1,
    letter: dir.replace(/[+-]/, ""),
});

// Cesium stores the nadir axis as the face pointing away from Earth, so +X at Earth is "-X"
const cesiumNadirName = (dir: string) => {
    const { sign, letter } = parseDirection(dir);
    return sign > 0 ? `-${letter}` : letter;
};

const cesiumOrbitName = (dir: string) => {
    const { sign, letter } = parseDirection(dir);
    return sign > 0 ? letter : `-${letter}`;
};

// Body direction in cube-local coordinates (AXES in thermalConstants.ts)
const bodyVector = (dir: string): THREE.Vector3 => {
    const { sign, letter } = parseDirection(dir);
    const axis = AXES.find((a) => a.label === letter);
    if (!axis) throw new Error(`Unknown body axis: ${dir}`);
    return new THREE.Vector3(...axis.dir).multiplyScalar(sign);
};

const buildCesiumOffset = (nadirName: string, orbitName: string): THREE.Quaternion => {
    const worldDown = new THREE.Vector3(0, 0, -1);
    const localNadir = CESIUM_LOCAL_AXIS[nadirName].clone().negate().normalize();
    const qNadir = new THREE.Quaternion().setFromUnitVectors(localNadir, worldDown);

    const worldOrbit = CESIUM_LOCAL_AXIS[orbitName].clone().applyQuaternion(qNadir);
    worldOrbit.addScaledVector(worldDown, -worldOrbit.dot(worldDown)).normalize();
    const qOrbit = new THREE.Quaternion().setFromUnitVectors(worldOrbit, new THREE.Vector3(1, 0, 0));

    return qOrbit.multiply(qNadir);
};

// Cesium world (Z up) to three.js world (Y up): -90 degrees about X, so Cesium down (-Z) becomes -Y
const CESIUM_WORLD_TO_THREE = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
// Cesium body-local to cube-local: 180 degrees about Z (its own inverse)
const CESIUM_BODY_TO_CUBE = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI);
// Applied to qBackend on the right: qBackend * qOffset * CESIUM_BODY_TO_CUBE
const ATTITUDE_CORRECTION = buildCesiumOffset(
    cesiumNadirName(THERMAL_BUS_AXES.nadirBodyDirection),
    cesiumOrbitName(THERMAL_BUS_AXES.orbitBodyDirection)
).multiply(CESIUM_BODY_TO_CUBE);

/** Body-to-world rotation for one sample: Cesium offset converted to three.js, before the start alignment. */
const rawOrientation = (sample: AttitudeSample): THREE.Quaternion => {
    const qBackend = new THREE.Quaternion(sample.x, sample.y, sample.z, sample.w);
    return CESIUM_WORLD_TO_THREE.clone().multiply(qBackend).multiply(ATTITUDE_CORRECTION);
};

/**
 * Fixed rotation for the whole body so that, at the first sample, the nadir (+X) face points down (-Y),
 * which is where the horizontal floor is. Later steps follow the attitude relative to this start.
 */
const alignmentFromSample = (sample: AttitudeSample): THREE.Quaternion => {
    const nadirAtStart = bodyVector(THERMAL_BUS_AXES.nadirBodyDirection)
        .applyQuaternion(rawOrientation(sample))
        .normalize();
    return new THREE.Quaternion().setFromUnitVectors(nadirAtStart, new THREE.Vector3(0, -1, 0));
};

// ---------------------------------------------------------------------------
// Earth-relative pose
// ---------------------------------------------------------------------------

/** Linear interpolation of the satellite ECEF position at a Unix-seconds time, clamped to the ends. */
const interpolatePosition = (p: PositionSeries, unixSec: number): THREE.Vector3 => {
    const { times, pos } = p;
    const last = times.length - 1;
    const at = (i: number) => new THREE.Vector3(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);
    if (unixSec <= times[0]) return at(0);
    if (unixSec >= times[last]) return at(last);
    let lo = 0, hi = last;
    while (lo < hi - 1) {
        const mid = (lo + hi) >> 1;
        if (times[mid] <= unixSec) lo = mid; else hi = mid;
    }
    const u = (unixSec - times[lo]) / (times[hi] - times[lo]);
    return at(lo).lerp(at(hi), u);
};

/**
 * Local orbital frame at a time, in ECEF, as a quaternion: x = nadir (toward Earth), y = along-track, z = x cross y.
 * Velocity comes from a central difference of the interpolated positions.
 */
const localFrameQuaternion = (p: PositionSeries, unixSec: number): THREE.Quaternion => {
    const h = 1; // seconds
    const pos = interpolatePosition(p, unixSec);
    const velocity = interpolatePosition(p, unixSec + h).sub(interpolatePosition(p, unixSec - h)).divideScalar(2 * h);
    const x = pos.clone().negate().normalize();
    const y = velocity.addScaledVector(x, -velocity.dot(x)).normalize();
    const z = x.clone().cross(y);
    return new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
};

/**
 * Rotation (in the three.js world) that cancels the orbital motion since the reference time, so a
 * nadir-pointing body stays still relative to the Earth direction. Identity at the reference time.
 */
const CESIUM_WORLD_TO_THREE_INVERSE = CESIUM_WORLD_TO_THREE.clone().invert();

const earthRelativeCorrection = (p: PositionSeries, frameRef: THREE.Quaternion, unixSec: number): THREE.Quaternion => {
    const frameNow = localFrameQuaternion(p, unixSec);
    const rotationEcef = frameRef.clone().multiply(frameNow.invert());
    // Express the ECEF rotation in the three.js frame: W * R * W^-1
    return CESIUM_WORLD_TO_THREE.clone().multiply(rotationEcef).multiply(CESIUM_WORLD_TO_THREE_INVERSE);
};

/** Cube (body group) orientation for a Unix-seconds time. Continuous, not snapped, so it does not flip near boundaries. */
export const thermalOrientationAt = (series: AttitudeSeries, unixSec: number): THREE.Quaternion => {
    const correction = series.positions && series.refFrame
        ? earthRelativeCorrection(series.positions, series.refFrame, unixSec)
        : new THREE.Quaternion();
    return series.alignment.clone().multiply(correction).multiply(rawOrientation(findNearestAttitude(series, unixSec)));
};

// ---------------------------------------------------------------------------
// Maneuver events and animation
// ---------------------------------------------------------------------------

const MANEUVER_CONFIG = {
    angleThresholdDeg: 70,        // smallest total change that counts as a maneuver
    rateThresholdDegPerSec: 0.25, // above slow drift, below a typical slew
    windowSamples: 3,             // angular rate is measured over this many samples
    gapSec: 5,                    // fast runs closer than this are joined into one
    animationSec: 10,             // fixed animation length, in simulation time
};

interface ManeuverEvent {
    /** Unix seconds when the maneuver starts */
    startSec: number;
    fromQ: THREE.Quaternion;
    toQ: THREE.Quaternion;
}

const angleDegBetween = (a: THREE.Quaternion, b: THREE.Quaternion) => a.angleTo(b) * (180 / Math.PI);


/**
 * Maneuver events for a series, in time order. Each event's fromQ is the previous event's toQ
 * (the pose the cube holds between maneuvers), and toQ is the pose at the end of the maneuver.
 */
export const buildManeuverTrack = (series: AttitudeSeries): ManeuverEvent[] => {
    const config = MANEUVER_CONFIG;
    const n = series.times.length;
    const k = config.windowSamples;
    if (n <= k) return [];

    const poses = Array.from(series.times, (t) => thermalOrientationAt(series, t));

    // Samples where the angular rate, measured over k samples, is above the threshold
    const fast: boolean[] = new Array(n).fill(false);
    for (let i = 0; i + k < n; i++) {
        const rate = angleDegBetween(poses[i], poses[i + k]) / (series.times[i + k] - series.times[i]);
        fast[i] = rate > config.rateThresholdDegPerSec;
    }

    // Group fast samples into runs, joining runs separated by at most gapSec
    const runs: Array<[number, number]> = [];
    for (let i = 0; i < n; i++) {
        if (!fast[i]) continue;
        const last = runs[runs.length - 1];
        if (last && series.times[i] - series.times[last[1]] <= config.gapSec) last[1] = i;
        else runs.push([i, i]);
    }

    const events: ManeuverEvent[] = [];
    for (const [s, e] of runs) {
        const b = Math.min(e + k, n - 1);
        if (angleDegBetween(poses[s], poses[b]) < config.angleThresholdDeg) continue;

        const prev = events[events.length - 1];
        if (prev && series.times[s] < prev.startSec + config.animationSec) {
            // Too close to the previous maneuver to animate separately: merge into it
            events[events.length - 1] = { startSec: prev.startSec, fromQ: prev.fromQ, toQ: poses[b] };
            continue;
        }
        const fromQ = prev ? prev.toQ : poses[0];
        events.push({ startSec: series.times[s], fromQ, toQ: poses[b] });
    }
    return events;
};

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/**
 * Body pose at a Unix-seconds time. Holds the previous pose between maneuvers, and during each maneuver
 * slerps from its start pose to its end pose over the animation length (simulation time).
 * startQ is used only when there are no events.
 */
export const poseAtTime = (track: ManeuverEvent[], unixSec: number, startQ: THREE.Quaternion): THREE.Quaternion => {
    if (track.length === 0) return startQ;

    // Last event that has started by unixSec (binary search; track is in time order)
    let lo = -1, hi = track.length - 1;
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (track[mid].startSec <= unixSec) lo = mid; else hi = mid - 1;
    }
    if (lo < 0) return track[0].fromQ;

    const event = track[lo];
    const progress = (unixSec - event.startSec) / MANEUVER_CONFIG.animationSec;
    if (progress >= 1) return event.toQ;
    return new THREE.Quaternion().slerpQuaternions(event.fromQ, event.toQ, easeInOut(Math.max(0, progress)));
};
